import { useRef, useState } from "react";
import { Download } from "lucide-react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { api, quarterLabel } from "../lib/api";

const BAND_STYLE = {
  Exceptional: "border-[#F3D059] text-[#F3D059]",
  Impact: "border-[#D4AF37] text-[#D4AF37]",
  Advanced: "border-[#D4AF37]/60 text-[#D4AF37]/90",
  Competent: "border-yellow-200/40 text-yellow-200/80",
  Emerging: "border-orange-400/50 text-orange-300",
  Foundation: "border-red-400/50 text-red-300",
};

const BandChip = ({ band }) =>
  band ? (
    <span className={`text-[9px] uppercase tracking-widest px-2 py-0.5 border ${BAND_STYLE[band]}`}>{band}</span>
  ) : null;

const QoQ = ({ value }) =>
  value === null || value === undefined ? null : (
    <span className={`text-xs font-bold ${value >= 0 ? "text-[#D4AF37]" : "text-red-400"}`}>
      {value >= 0 ? `+${value}` : value} Q/Q
    </span>
  );

const Bar = ({ score }) => (
  <div className="h-1 bg-white/10 flex-1">
    <div className="h-1 bg-[#D4AF37] transition-all duration-500" style={{ width: `${score || 0}%` }} />
  </div>
);

const loadImageAsDataUrl = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      resolve({ dataUrl: canvas.toDataURL("image/png"), w: img.naturalWidth, h: img.naturalHeight });
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });

const fetchPhotoAsDataUrl = async (playerId) => {
  try {
    const { data } = await api.get(`/players/${playerId}/photo`, { responseType: "blob" });
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.onload = () => resolve({ dataUrl: reader.result, w: img.naturalWidth, h: img.naturalHeight });
        img.onerror = () => resolve(null);
        img.src = reader.result;
      };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(data);
    });
  } catch {
    return null;
  }
};

const drawCoverPage = async (pdf, data) => {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  // Dark background
  pdf.setFillColor(5, 5, 5);
  pdf.rect(0, 0, pageWidth, pageHeight, "F");

  // Subtle gold border frame
  pdf.setDrawColor(212, 175, 55);
  pdf.setLineWidth(1);
  pdf.rect(30, 30, pageWidth - 60, pageHeight - 60, "S");

  // Fetch player photo up-front so we can pick the right cover layout
  const photo = data.player_id ? await fetchPhotoAsDataUrl(data.player_id) : null;
  const logo = await loadImageAsDataUrl("/logo.png");

  if (photo) {
    // --- Layout A: player photo dominant, small logo top-centre ---
    if (logo) {
      const logoW = 70;
      const logoH = (logo.h / logo.w) * logoW;
      pdf.addImage(logo.dataUrl, "PNG", (pageWidth - logoW) / 2, 65, logoW, logoH);
    }
    // Big rectangular photo (portrait 4:5)
    const photoW = 220;
    const photoH = 275;
    const px = (pageWidth - photoW) / 2;
    const py = 150;
    // Gold border frame
    pdf.setDrawColor(212, 175, 55);
    pdf.setLineWidth(2);
    pdf.rect(px - 3, py - 3, photoW + 6, photoH + 6, "S");
    pdf.addImage(photo.dataUrl, "JPEG", px, py, photoW, photoH);
    // Title block below
    const tY = py + photoH + 45;
    pdf.setTextColor(255, 255, 255);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(22);
    pdf.text("PLAYER DEVELOPMENT REPORT", pageWidth / 2, tY, { align: "center" });
    pdf.setTextColor(212, 175, 55);
    pdf.setFontSize(13);
    pdf.text(quarterLabel(data.quarter).toUpperCase(), pageWidth / 2, tY + 20, { align: "center" });
    pdf.setTextColor(255, 255, 255);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(16);
    pdf.text((data.player_name || "").toUpperCase(), pageWidth / 2, tY + 42, { align: "center" });
    const subLine = [data.age_group, data.batch, data.position].filter(Boolean).join("  ·  ");
    if (subLine) {
      pdf.setTextColor(160, 160, 160);
      pdf.setFontSize(9);
      pdf.text(subLine, pageWidth / 2, tY + 60, { align: "center" });
    }
  } else {
    // --- Layout B: no player photo — big centred logo fills the empty space ---
    if (logo) {
      const logoW = 240;
      const logoH = (logo.h / logo.w) * logoW;
      const lx = (pageWidth - logoW) / 2;
      const ly = pageHeight * 0.22;
      pdf.addImage(logo.dataUrl, "PNG", lx, ly, logoW, logoH);
      // Thin gold divider under logo
      pdf.setDrawColor(212, 175, 55);
      pdf.setLineWidth(0.5);
      pdf.line(pageWidth * 0.35, ly + logoH + 30, pageWidth * 0.65, ly + logoH + 30);
      // Title
      const tY = ly + logoH + 65;
      pdf.setTextColor(255, 255, 255);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(26);
      pdf.text("PLAYER DEVELOPMENT REPORT", pageWidth / 2, tY, { align: "center" });
      pdf.setTextColor(212, 175, 55);
      pdf.setFontSize(15);
      pdf.text(quarterLabel(data.quarter).toUpperCase(), pageWidth / 2, tY + 26, { align: "center" });
      pdf.setTextColor(255, 255, 255);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(18);
      pdf.text((data.player_name || "").toUpperCase(), pageWidth / 2, tY + 56, { align: "center" });
      const subLine = [data.age_group, data.batch, data.position].filter(Boolean).join("  ·  ");
      if (subLine) {
        pdf.setTextColor(160, 160, 160);
        pdf.setFontSize(10);
        pdf.text(subLine, pageWidth / 2, tY + 78, { align: "center" });
      }
    }
  }

  // Footer strapline (both layouts)
  pdf.setTextColor(212, 175, 55);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(9);
  pdf.text("ZAVI FOOTBALL CLUB   ·   ZEAL  ·  AGILITY  ·  VISION  ·  INTENSITY", pageWidth / 2, pageHeight - 55, { align: "center" });

  if (data.eval_date) {
    pdf.setTextColor(120, 120, 120);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.text(`Evaluated on ${data.eval_date}${data.coach ? ` · Coach ${data.coach}` : ""}`, pageWidth / 2, pageHeight - 40, { align: "center" });
  }
};

export const ReportView = ({ data, testid = "report-view" }) => {
  const nodeRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  const download = async () => {
    if (!nodeRef.current) return;
    setDownloading(true);
    try {
      const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 24;
      const contentWidth = pageWidth - 2 * margin;
      const maxContentHeight = pageHeight - 2 * margin;

      // ---- Cover Page ----
      await drawCoverPage(pdf, data);

      // ---- Content Pages (section-by-section to avoid mid-row cuts) ----
      const sections = nodeRef.current.querySelectorAll(".pdf-section");
      pdf.addPage();
      pdf.setFillColor(10, 10, 10);
      pdf.rect(0, 0, pageWidth, pageHeight, "F");
      let cursorY = margin;

      for (const section of sections) {
        const c = await html2canvas(section, {
          backgroundColor: "#0A0A0A",
          scale: 2,
          useCORS: true,
          windowWidth: 900,
        });
        const imgData = c.toDataURL("image/jpeg", 0.95);
        const w = contentWidth;
        const h = (c.height * w) / c.width;

        if (h > maxContentHeight) {
          // Section is taller than a page — split by fixed rows (unavoidable slicing, but rare)
          let heightLeft = h;
          let position = 0;
          // Fresh page start if not already at top
          if (cursorY !== margin) {
            pdf.addPage();
            pdf.setFillColor(10, 10, 10);
            pdf.rect(0, 0, pageWidth, pageHeight, "F");
            cursorY = margin;
          }
          const firstSlice = Math.min(maxContentHeight, heightLeft);
          pdf.addImage(imgData, "JPEG", margin, cursorY - position, w, h);
          heightLeft -= firstSlice;
          position += firstSlice;
          while (heightLeft > 0) {
            pdf.addPage();
            pdf.setFillColor(10, 10, 10);
            pdf.rect(0, 0, pageWidth, pageHeight, "F");
            pdf.addImage(imgData, "JPEG", margin, margin - position, w, h);
            heightLeft -= maxContentHeight;
            position += maxContentHeight;
          }
          cursorY = margin;
          continue;
        }

        if (cursorY + h > pageHeight - margin) {
          pdf.addPage();
          pdf.setFillColor(10, 10, 10);
          pdf.rect(0, 0, pageWidth, pageHeight, "F");
          cursorY = margin;
        }
        pdf.addImage(imgData, "JPEG", margin, cursorY, w, h);
        cursorY += h + 10;
      }

      const safeName = (data.player_name || "player").replace(/[^a-z0-9]+/gi, "_");
      pdf.save(`Zavi_Report_${safeName}_${data.quarter}.pdf`);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="border border-white/10 bg-[#0A0A0A]" data-testid={testid}>
      <div className="no-print flex justify-end px-6 pt-4">
        <button onClick={download} disabled={downloading}
          className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#D4AF37] border border-[#D4AF37]/50 px-4 py-2 hover:bg-[#D4AF37] hover:text-black transition-colors duration-200 disabled:opacity-50"
          data-testid={`${testid}-download-pdf`}>
          <Download size={12} /> {downloading ? "Preparing..." : "Download PDF"}
        </button>
      </div>
      <div ref={nodeRef} className="bg-[#0A0A0A]">
        <div className="pdf-section p-6 sm:p-8 border-b border-white/10 flex flex-wrap items-center gap-5">
          <img src="/logo.png" alt="" crossOrigin="anonymous" className="h-14 w-auto" />
          <div className="flex-1 min-w-[200px]">
            <p className="text-[10px] tracking-[0.25em] uppercase text-[#D4AF37] font-semibold">Quarterly Player Development Review</p>
            <h3 className="font-heading text-2xl font-black uppercase tracking-tight mt-1" data-testid={`${testid}-player`}>{data.player_name}</h3>
            <p className="text-white/50 text-xs mt-1">
              {[data.age_group, data.detail && data.batch, data.position, data.strong_foot && `${data.strong_foot} foot`].filter(Boolean).join(" · ")}
            </p>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-widest text-white/40">Evaluation Cycle</p>
            <p className="font-heading text-lg font-bold text-[#D4AF37]">{quarterLabel(data.quarter)}</p>
            {data.eval_date && <p className="text-white/40 text-xs">{data.eval_date}{data.coach ? ` · Coach ${data.coach}` : ""}</p>}
          </div>
        </div>

        <div className="pdf-section p-6 sm:p-8 border-b border-white/10 flex flex-wrap items-center gap-8">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Player Development Index</p>
            <div className="flex items-end gap-3">
              <span className="font-heading text-6xl font-black text-[#D4AF37]" data-testid={`${testid}-zpdi`}>{data.zpdi ?? "—"}</span>
              <span className="text-white/40 text-lg mb-1">/100</span>
              <BandChip band={data.zpdi_band} />
            </div>
          </div>
          {data.zpdi_qoq !== null && data.zpdi_qoq !== undefined && data.trajectory.length > 1 && (
            <div>
              <p className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Quarter-Over-Quarter</p>
              <p className="font-heading text-3xl font-black">
                <QoQ value={data.zpdi_qoq} />
              </p>
              <p className="text-white/40 text-xs mt-1">from {data.trajectory[data.trajectory.length - 2].zpdi} to {data.zpdi}</p>
            </div>
          )}
        </div>

        <div className="pdf-section grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border-b border-white/10">
          {data.pillars.map((p) => (
            <div key={p.name} className="bg-[#0A0A0A] p-5" data-testid={`${testid}-pillar-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <p className="text-[10px] uppercase tracking-widest text-white/40 mb-2">{p.name}</p>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-heading text-3xl font-black">{p.score ?? "—"}</span>
                <BandChip band={p.band} />
              </div>
              <div className="mt-2 flex items-center gap-2">
                <Bar score={p.score} />
                <QoQ value={p.qoq} />
              </div>
            </div>
          ))}
        </div>

        {data.gii !== null && (
          <div className="pdf-section p-6 sm:p-8 border-b border-white/10 flex flex-wrap items-center gap-6">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Zavi Game Intelligence Index</p>
              <div className="flex items-center gap-3">
                <span className="font-heading text-4xl font-black text-[#D4AF37]">{data.gii}</span>
                <BandChip band={data.gii_band} />
              </div>
            </div>
            <p className="text-white/40 text-xs max-w-md leading-relaxed">
              Built from scanning, movement, decision speed, positional discipline, pressing awareness and transition response. The index is not a test percentage — it shows current development relative to Zavi benchmarks for this age and position.
            </p>
          </div>
        )}

        {data.detail.map((pillar) => (
          <div key={pillar.pillar} className="pdf-section border-b border-white/10">
            <p className="px-6 sm:px-8 pt-6 pb-2 text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold">
              {pillar.pillar} / {data.pillars.find((x) => x.name === pillar.pillar)?.score ?? "—"}
            </p>
            {pillar.groups.map((g) => (
              <div key={g.name} className="px-6 sm:px-8 py-4">
                <p className="text-[10px] uppercase tracking-widest text-white/40 mb-3">{g.name}</p>
                <div className="space-y-2.5">
                  {g.metrics.map((m) => (
                    <div key={m.key} className="grid grid-cols-[minmax(140px,1.8fr)_minmax(120px,2fr)_auto_auto] items-center gap-3 text-sm" data-testid={`${testid}-metric-${m.key}`}>
                      <span className="text-white/75 leading-snug pr-1 break-words">{m.label}</span>
                      <Bar score={m.score} />
                      <span className="font-heading font-bold w-9 text-right tabular-nums">{m.score ?? "—"}</span>
                      <div className="w-24 flex justify-end"><BandChip band={m.band} /></div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}

        {data.trajectory.length > 0 && (
          <div className="pdf-section p-6 sm:p-8 border-b border-white/10">
            <p className="text-[10px] uppercase tracking-widest text-white/40 mb-4">Development Trajectory</p>
            {data.trajectory.length === 1 ? (
              // Single data point — treat this as a "baseline set" panel instead of a lonely bar
              <div className="grid sm:grid-cols-[auto_1fr] gap-6 items-center">
                <div className="border border-[#D4AF37]/40 bg-[#0F0F0F] px-8 py-6 text-center">
                  <p className="text-[10px] uppercase tracking-widest text-white/40">Baseline</p>
                  <p className="font-heading text-5xl font-black text-[#D4AF37] mt-1">{data.trajectory[0].zpdi}</p>
                  <p className="text-white/50 text-xs mt-2 uppercase tracking-widest">{quarterLabel(data.trajectory[0].quarter)}</p>
                </div>
                <div>
                  <p className="font-heading text-lg font-black uppercase tracking-tight">First evaluation cycle</p>
                  <p className="text-white/60 text-sm mt-2 leading-relaxed max-w-md">
                    This report sets the baseline. Every future quarter is measured relative to this point — quarter-over-quarter change, band shifts and category progress will appear here from the next review onwards.
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-end gap-4 flex-wrap">
                {data.trajectory.map((t) => (
                  <div key={t.quarter} className="text-center">
                    <p className="font-heading font-black text-[#D4AF37] mb-1">{t.zpdi}</p>
                    <div className="w-12 bg-[#D4AF37]/70 mx-auto" style={{ height: `${Math.max(8, (t.zpdi || 0) * 0.8)}px` }} />
                    <p className="text-white/40 text-[10px] mt-2 uppercase">{quarterLabel(t.quarter)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="pdf-section p-6 sm:p-8 grid sm:grid-cols-3 gap-6 border-b border-white/10">
          {data.doing_well && (
            <div>
              <p className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-semibold mb-2">Doing Well</p>
              <p className="text-white/60 text-sm leading-relaxed whitespace-pre-wrap">{data.doing_well}</p>
            </div>
          )}
          {data.next_quarter && (
            <div>
              <p className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-semibold mb-2">Next Quarter's Focus</p>
              <p className="text-white/60 text-sm leading-relaxed whitespace-pre-wrap">{data.next_quarter}</p>
            </div>
          )}
          {data.parent_support && (
            <div>
              <p className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-semibold mb-2">How You Can Support at Home</p>
              <p className="text-white/60 text-sm leading-relaxed whitespace-pre-wrap">{data.parent_support}</p>
            </div>
          )}
        </div>

        {data.coach_summary && (
          <div className="pdf-section p-6 sm:p-8">
            <p className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-semibold mb-2">Coach Summary</p>
            <p className="text-white/70 text-sm leading-relaxed whitespace-pre-wrap font-heading text-base font-light">{data.coach_summary}</p>
          </div>
        )}
      </div>
    </div>
  );
};
